/**
 * DECKS STORE
 *
 * Shape (persisted under the key "decks"):
 *   saved       SavedDeck[]     the player's own decks, newest edit first
 *   lastUsedId  string | null   deck preselected in the PLAY hub
 *
 * A DeckEntry is what the rest of the app consumes: a saved deck or one of the
 * built-in decks from src/data/decks. Built-ins carry `builtIn: true` and are
 * read-only, so `rename`, `update` and `remove` refuse them; `duplicate` turns
 * one into an editable copy. Only `saved` is persisted, because built-ins live
 * in the card data and would go stale in storage.
 *
 * Other engineers: the deck builder edits through `update`, and any deck id in
 * `list()` is safe to hand to `deckCards(id)` for createGame.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DeckDefinition, Faction } from '@/engine';
import { expandDeck } from '@/engine';
import { allDecks, getDeck } from '@/data';
import { adapterStorage } from '@/persist';

export interface DeckCardEntry {
  id: string;
  count: number;
}

export interface SavedDeck {
  id: string;
  name: string;
  description?: string;
  factions: Faction[];
  archetype: string;
  cards: DeckCardEntry[];
  createdAt: number;
  updatedAt: number;
}

export interface DeckEntry extends SavedDeck {
  /** Built-in decks come from src/data/decks and cannot be edited. */
  builtIn: boolean;
  /** Developer decks are hidden from normal play unless dev mode is on. */
  dev: boolean;
}

export interface DecksState {
  saved: SavedDeck[];
  lastUsedId: string | null;
}

export interface DecksActions {
  /** Built-in decks first, then saved decks. Dev decks last. */
  list(): DeckEntry[];
  get(id: string): DeckEntry | undefined;
  create(input: { name: string; factions?: Faction[]; archetype?: string; cards?: DeckCardEntry[] }): DeckEntry;
  duplicate(id: string, name?: string): DeckEntry | undefined;
  rename(id: string, name: string): boolean;
  update(id: string, patch: Partial<Omit<SavedDeck, 'id' | 'createdAt'>>): boolean;
  remove(id: string): boolean;
  setLastUsed(id: string | null): void;
  reset(): void;
}

export const DEFAULT_DECKS: DecksState = { saved: [], lastUsedId: null };

export type DecksStore = DecksState & DecksActions;

function fromDefinition(def: DeckDefinition): DeckEntry {
  return {
    id: def.id,
    name: def.name,
    description: def.description,
    factions: def.factions ?? [],
    archetype: def.archetype ?? 'midrange',
    cards: (def.cards ?? []).map((c) => ({ id: c.id, count: c.count })),
    createdAt: 0,
    updatedAt: 0,
    builtIn: true,
    dev: def.dev === true,
  };
}

/** The read-only built-in decks, in a stable order with dev decks last. */
export function builtInDecks(): DeckEntry[] {
  return allDecks
    .map(fromDefinition)
    .sort((a, b) => Number(a.dev) - Number(b.dev) || a.name.localeCompare(b.name));
}

function newId(): string {
  return `deck-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`;
}

export const useDecks = create<DecksStore>()(
  persist(
    (set, get) => ({
      ...DEFAULT_DECKS,
      list() {
        return [...builtInDecks(), ...get().saved.map((d) => ({ ...d, builtIn: false, dev: false }))];
      },
      get(id) {
        return get().list().find((d) => d.id === id);
      },
      create(input) {
        const now = Date.now();
        const deck: SavedDeck = {
          id: newId(),
          name: input.name,
          factions: input.factions ?? [],
          archetype: input.archetype ?? 'midrange',
          cards: input.cards ?? [],
          createdAt: now,
          updatedAt: now,
        };
        set({ saved: [...get().saved, deck] });
        return { ...deck, builtIn: false, dev: false };
      },
      duplicate(id, name) {
        const source = get().get(id);
        if (!source) return undefined;
        return get().create({
          name: name ?? `${source.name} copy`,
          factions: source.factions,
          archetype: source.archetype,
          cards: source.cards.map((c) => ({ ...c })),
        });
      },
      rename(id, name) {
        return get().update(id, { name });
      },
      update(id, patch) {
        const saved = get().saved;
        const index = saved.findIndex((d) => d.id === id);
        if (index < 0) return false;
        const next = saved.slice();
        next[index] = { ...next[index], ...patch, id, updatedAt: Date.now() };
        set({ saved: next });
        return true;
      },
      remove(id) {
        const saved = get().saved;
        const next = saved.filter((d) => d.id !== id);
        if (next.length === saved.length) return false;
        set({ saved: next, lastUsedId: get().lastUsedId === id ? null : get().lastUsedId });
        return true;
      },
      setLastUsed(id) {
        set({ lastUsedId: id });
      },
      reset() {
        set({ ...DEFAULT_DECKS });
      },
    }),
    {
      name: 'decks',
      storage: adapterStorage<Pick<DecksStore, 'saved' | 'lastUsedId'>>(),
      partialize: (state) => ({ saved: state.saved, lastUsedId: state.lastUsedId }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted as Partial<DecksState> | undefined),
      }),
    },
  ),
);

/** Flat card id list for createGame, for any deck id in `list()`. */
export function deckCards(id: string): string[] {
  const builtIn = getDeck(id);
  if (builtIn) return expandDeck(builtIn.cards ?? []);
  const saved = useDecks.getState().saved.find((d) => d.id === id);
  return saved ? expandDeck(saved.cards) : [];
}
